
import { Component, Vue, Watch } from 'vue-property-decorator';

import AutoCompleteField from '../../../../framework/fields/AutoCompleteField.vue';
import SelectField from '../../../../framework/fields/SelectField.vue';
import { sharedState } from '../../../../framework/state';
import { getEnumerationOptions, getOptionLabel } from '../../../../framework/service/options';
import { deleteResource, getResourcePagingNew, getResourcesNew } from '../../../../framework/client/resource';

import { Vendor } from '../../../../../common/application/model/Vendor';
import { VendorType } from '../../../../../common/application/model/enums/VendorType';
import { FrameworkUserRole } from '../../../../../common/framework/enumeration/FrameworkUserRole';

import AutoCompleteMultipleField from '../../../fields/AutoCompleteMultipleField.vue';

import { getVendorById, postVendor } from '../../../client/netvisor';
import { ApplicationUserRole } from '../../../../../common/application/enumeration/ApplicationUserRole';
import { ApplicationResource } from '../../../../../common/application/enumeration/ApplicationResource';
import { InvoicePeriodizationType } from '../../../../../common/application/model/enums/InvoicePeriodizationType';
import { errorToast, successToast } from '../../../service/toast_service';
import ParameterService from '../../../service/parameter_service';

@Component({
    components: {
        SelectField,
        AutoCompleteField,
        AutoCompleteMultipleField,
    },
})
export default class Vendors extends Vue {
    readonly resourceType = ApplicationResource.VENDOR;
    parameterService = new ParameterService(this.resourceType);

    shared = sharedState;
    local = {
        rows: [] as Vendor[],
        total: 0,
        loading: false,
        selected: undefined as Vendor | undefined,
        vendorTypeOptions: [] as { id: string | undefined; label: string }[],
        invoicePeriodizationTypeOptions: [] as { id: string | undefined; label: string }[],
        parameters: this.parameterService.get(),
    };

    async mounted() {
        this.local.vendorTypeOptions = getEnumerationOptions('VendorType', Object.keys(VendorType), this);
        this.local.invoicePeriodizationTypeOptions = getEnumerationOptions(
            'InvoicePeriodizationType',
            Object.keys(InvoicePeriodizationType),
            this,
        );
        await this.loadAsyncData();
    }

    async loadAsyncData() {
        this.local.loading = true;

        const backendParameters = this.parameterService.getBackendParameters();
        this.local.total = (await getResourcePagingNew(this.resourceType, backendParameters)).rowCount;

        this.local.rows = [];
        const rows = (await getResourcesNew(
            this.resourceType,
            this.local.parameters.page,
            backendParameters,
        )) as Array<Vendor>;

        rows.forEach((row) => {
            this.local.rows.push(row);
        });

        if (this.local && this.local.rows.indexOf(this.local.selected!!) == -1) {
            this.local.selected = undefined;
        }

        this.local.loading = false;
    }

    async onSortChange(field: string, order: 'desc' | 'asc') {
        this.local.parameters.sortField = field;
        this.local.parameters.sortOrder = order;
        await this.resetPagingAndLoadData();
    }

    async onPageChange(page: number) {
        this.local.parameters.page = page;
        this.parameterService.set(this.local.parameters);
        await this.loadAsyncData();
    }

    @Watch('local.parameters.vendorType')
    selectedVendorTypeChange() {
        if (this.local.loading) {
            return;
        }

        this.resetPagingAndLoadData();
    }

    dropdownClosed(resourceIds: string[], idField: string): void {
        if (this.local.loading) {
            return;
        }

        if (!resourceIds.length) {
            delete this.local.parameters[idField];
        } else {
            this.local.parameters[idField] = resourceIds;
        }

        this.resetPagingAndLoadData();
    }

    async resetFilter(): Promise<void> {
        this.local.parameters = this.parameterService.getDefault();
        await this.resetPagingAndLoadData();
    }

    add() {
        this.$router.push('/vendor/add');
    }

    edit(id?: string) {
        if (id) {
            this.$router.push('/vendor/' + id + '/edit');
        } else if (this.local.selected) {
            this.$router.push('/vendor/' + this.local.selected.id + '/edit');
        }
    }

    confirmDelete() {
        this.$buefy.dialog.confirm({
            title: this.$t('title.confirmDelete').toString(),
            message: this.$t('message.confirmDelete').toString(),
            cancelText: this.$t('button.cancel').toString(),
            confirmText: this.$t('button.ok').toString(),
            type: 'is-success',
            onConfirm: async () => {
                if (this.local.selected) {
                    try {
                        await deleteResource(this.resourceType, this.local.selected.id);
                        successToast(this, 'message.resourceDeleted');
                        await this.loadAsyncData();
                    } catch (e) {
                        errorToast(this);
                    }
                }
            },
        });
    }

    async getNetvisorInfo(vendorId: string) {
        await getVendorById(vendorId);
        await this.loadAsyncData();
    }

    async sendToNetvisor(vendorId: any) {
        try {
            this.local.loading = true;
            await postVendor(vendorId);
            this.local.loading = false;
            await this.loadAsyncData();
        } catch (error) {
            this.local.loading = false;
        }
    }

    getVendorTypeLabel(id: string): string {
        return getOptionLabel(id, this.local.vendorTypeOptions);
    }

    getInvoicePeriodizationType(id: string): string {
        return getOptionLabel(id, this.local.invoicePeriodizationTypeOptions);
    }

    vendorSelectorParameters(): Map<string, string> {
        const parameters = new Map<string, string>();

        if (this.local.parameters.vendorType) {
            parameters.set('vendorType', this.local.parameters.customerType);
        }

        if (this.local.parameters.id) {
            parameters.set('id', this.local.parameters.id[0]);
        }

        return parameters;
    }

    view() {
        if (this.local.selected) {
            this.$router.push(`/vendor/${this.local.selected.id}`);
        }
    }

    get isAdmin(): boolean {
        return this.shared.admin;
    }

    get isManager(): boolean {
        return this.shared.hasRole(ApplicationUserRole.MANAGER);
    }

    get vendorIds(): string[] {
        return this.local.parameters.id || [];
    }

    onRowClick(row: Vendor): void {
        if (this.local.selected && row.id === this.local.selected.id) {
            this.local.selected = undefined;
        }
    }

    private async resetPagingAndLoadData() {
        this.local.parameters.page = 1;
        this.parameterService.set(this.local.parameters);
        await this.loadAsyncData();
    }
}
